import { useEffect, useMemo, useState } from 'react';

import { OrderShort } from '~/api/Order';
import { usePreviousValue } from '~/components/MobileHeaderCard/hooks';
import { OrdersTab } from '~/pages/Orders/interfaces';
import { orderStore } from '~/stores';
import { ORDERS_TAB_STATUSES } from '~/stores/OrderStore';

export const useOrders = (props?: {
  tab?: OrdersTab;
  runObserver?: () => void;
  stopObserver?: () => void;
}) => {
  const { runObserver, stopObserver, tab } = props || {};

  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [orderList, setOrderList] = useState<OrderShort[]>([]);

  useEffect(() => {
    const page = orderStore.currentOrdersPage;
    const filters = tab ? { status: ORDERS_TAB_STATUSES[tab] } : undefined;

    const setLoadingStatus = (value: boolean) => {
      if (page > 1) {
        setIsPageLoading(value);
      } else {
        setIsLoading(value);
      }
    };

    setLoadingStatus(true);

    orderStore
      .requestPaginatedOrders({
        page,
        filters,
      })
      .then((response) => {
        setLoadingStatus(false);

        if (!response) {
          setOrderList([]);
          return;
        }

        const { result, hasNext } = response;

        orderStore.setDownloadIsPossible(hasNext);
        setOrderList((prev) => {
          if (page === 1) {
            return result;
          }

          return [...prev, ...result];
        });

        if (orderStore.downloadIsPossible && page === 1) {
          runObserver?.();
        } else if (!orderStore.downloadIsPossible) {
          stopObserver?.();
        }
      })
      .catch(() => {
        setLoadingStatus(false);
      });
  }, [orderStore.currentOrdersPage, tab]);

  usePreviousValue(tab, (newValue, prevValue) => {
    if (newValue !== prevValue) {
      setOrderList([]);
      orderStore.currentOrdersPage = 1;
    }
  });

  useEffect(
    () => () => {
      setOrderList([]);
      setIsPageLoading(false);
      setIsLoading(false);
    },
    [],
  );

  const activeOrders = useMemo(
    () =>
      orderList.filter(({ public_status }) =>
        ORDERS_TAB_STATUSES[OrdersTab.ACTIVE].includes(public_status),
      ),
    [orderList],
  );
  const finishedOrders = useMemo(
    () =>
      orderList.filter(({ public_status }) =>
        ORDERS_TAB_STATUSES[OrdersTab.HISTORY].includes(public_status),
      ),
    [orderList],
  );

  return {
    isLoading,
    orderList,
    setOrderList,
    isPageLoading,
    activeOrders,
    finishedOrders,
  };
};
